import {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import * as uuid from 'uuid/v4';

import {ActionButton, Icon, Input, Select, Toggle} from 'components';
import {CustomField} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {PRE_SESSION_FLOW_LIMITS, PreSessionFormField, PreSessionFormFieldInputType} from 'types';

const T_KEYS = TRANSLATION_KEYS.USER.CONTENT.CLIENT_SESSION.PRE_SESSION_FLOW;

type Props = {
    fields: PreSessionFormField[];
    userInfoFields: CustomField[];
    disabled: boolean;
    onChange: (fields: PreSessionFormField[]) => void;
};

const buildEmptyField = (): PreSessionFormField => ({
    id: uuid(),
    label: '',
    inputType: PreSessionFormFieldInputType.Text,
    required: true,
});

// Derive UI input type from a user-info schema field. Email/phone get specialised inputs based
// on naming + format hints; enum becomes a Select with the enum values; everything else is Text.
const deriveInputType = (
    schemaField: CustomField,
): {inputType: PreSessionFormFieldInputType; options?: string[]} => {
    if (Array.isArray(schemaField.enum) && schemaField.enum.length > 0) {
        return {inputType: PreSessionFormFieldInputType.Select, options: schemaField.enum};
    }
    if (schemaField.format === 'email' || schemaField.name.toLowerCase().includes('email')) {
        return {inputType: PreSessionFormFieldInputType.Email};
    }
    if (schemaField.format === 'phone' || schemaField.name.toLowerCase().includes('phone')) {
        return {inputType: PreSessionFormFieldInputType.Phone};
    }
    return {inputType: PreSessionFormFieldInputType.Text};
};

export const FormFieldsEditor: React.FC<Props> = ({fields, userInfoFields, disabled, onChange}) => {
    const {t} = useTranslation();

    const usedPaths = useMemo(
        () => new Set(fields.map((f) => f.label).filter(Boolean)),
        [fields],
    );

    const pathOptions = useMemo(() => userInfoFields.map((f) => ({
        id: f.name,
        name: f.title ? `${f.title} (${f.name})` : f.name,
    })), [userInfoFields]);

    const updateAt = useCallback((index: number, next: PreSessionFormField) => {
        const updated = fields.map((f, i) => (i === index ? next : f));
        onChange(updated);
    }, [fields, onChange]);

    const removeAt = useCallback((index: number) => {
        onChange(fields.filter((_, i) => i !== index));
    }, [fields, onChange]);

    const addField = useCallback(() => {
        onChange([...fields, buildEmptyField()]);
    }, [fields, onChange]);

    const allFieldsUsed = userInfoFields.length > 0 && usedPaths.size >= userInfoFields.length;

    return (
        <div className='tw-flex tw-flex-col tw-gap-2 tw-w-full'>
            {fields.map((field, index) => {
                const matchedSchemaField = userInfoFields.find((f) => f.name === field.label);
                const pathValue = pathOptions.find((o) => o.id === field.label);
                const schemaRequired = !!matchedSchemaField?.required;
                const effectiveRequired = schemaRequired || !!field.required;
                const availableOptions = pathOptions.filter(
                    (o) => o.id === field.label || !usedPaths.has(o.id),
                );

                return (
                    <div
                        key={field.id}
                        className='tw-flex tw-flex-col tw-gap-2 tw-py-1'
                    >
                        <div className='tw-flex tw-flex-wrap tw-items-center tw-gap-3'>
                            <div className='tw-flex-1 tw-min-w-[180px]'>
                                <Select
                                    placeholder={t(T_KEYS.FIELD.PATH_LABEL)}
                                    options={availableOptions}
                                    value={pathValue}
                                    required
                                    disabled={disabled}
                                    onChange={({id}: {id: string}) => {
                                        const nextSchemaField = userInfoFields.find((f) => f.name === id);
                                        const derived = nextSchemaField
                                            ? deriveInputType(nextSchemaField)
                                            : {inputType: field.inputType, options: field.options};
                                        updateAt(index, {
                                            ...field,
                                            label: id,
                                            inputType: derived.inputType,
                                            options: derived.options,
                                            required: nextSchemaField?.required ? true : field.required,
                                        });
                                    }}
                                />
                            </div>
                            <div className='tw-flex-shrink-0 tw-self-center'>
                                <Toggle
                                    name={`field-${field.id}-required`}
                                    checked={effectiveRequired}
                                    disabled={disabled || schemaRequired}
                                    label={t(T_KEYS.FIELD.REQUIRED)}
                                    labelPlacement='right'
                                    size='small'
                                    onChange={(required: boolean) => updateAt(index, {...field, required})}
                                />
                            </div>
                            <Icon
                                name='delete'
                                size='md'
                                button
                                color='alert'
                                margin={false}
                                disabled={disabled}
                                title={t(T_KEYS.FIELD.REMOVE)}
                                onClick={() => removeAt(index)}
                            />
                        </div>

                        {field.inputType === PreSessionFormFieldInputType.Select && !matchedSchemaField?.enum && (
                            <Input
                                label={t(T_KEYS.FIELD.OPTIONS_LABEL)}
                                description={t(T_KEYS.FIELD.OPTIONS_DESCRIPTION)}
                                value={(field.options ?? []).join(', ')}
                                maxLength={PRE_SESSION_FLOW_LIMITS.FORM_FIELD_OPTIONS_MAX
                                    * (PRE_SESSION_FLOW_LIMITS.FORM_FIELD_OPTION_MAX + 2)}
                                disabled={disabled}
                                onChange={(value: string) => updateAt(index, {
                                    ...field,
                                    options: value.split(',').map((s) => s.trim()).filter(Boolean),
                                })}
                            />
                        )}
                    </div>
                );
            })}

            <div>
                <ActionButton
                    action='create'
                    width='auto'
                    disabled={disabled || allFieldsUsed
                        || fields.length >= PRE_SESSION_FLOW_LIMITS.FORM_FIELDS_MAX}
                    onClick={addField}
                >
                    {t(T_KEYS.FIELD.ADD)}
                </ActionButton>
            </div>
        </div>
    );
};
